import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { UserMe } from "@/types";

/**
 * β-8 fix: Edge 추적 방지 / 시크릿 모드 등 localStorage 차단 환경에서도
 * zustand persist 가 throw 안 하게 안전 storage 래퍼.
 */
const safeStorage = {
  getItem: (name: string): string | null => {
    if (typeof window === "undefined") return null;
    try {
      return window.localStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name: string, value: string): void => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(name, value);
    } catch {
      // 추적 방지·quota 초과 등 — 무시 (메모리에만 보관)
    }
  },
  removeItem: (name: string): void => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.removeItem(name);
    } catch {
      // 무시
    }
  },
};

interface AuthState {
  token: string | null;
  user: UserMe | null;
  isLoggedIn: boolean;
  /** zustand persist hydration 완료 여부 — 새로고침 시 인증 가드 redirect 타이밍 정확화 (β-8) */
  _hasHydrated: boolean;
  setHasHydrated: (h: boolean) => void;
  setToken: (token: string) => void;
  setUser: (user: UserMe) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      isLoggedIn: false,
      _hasHydrated: false,
      setHasHydrated: (h) => set({ _hasHydrated: h }),

      setToken: (token) => {
        if (typeof window !== "undefined") {
          try { localStorage.setItem("access_token", token); } catch {}
        }
        set({ token, isLoggedIn: true });
      },

      setUser: (user) => set({ user }),

      logout: () => {
        // 앱 토큰/스토어 초기화
        if (typeof window !== "undefined") {
          try { localStorage.removeItem("access_token"); } catch {}
        }
        set({ token: null, user: null, isLoggedIn: false });

        // 카카오 세션까지 끊기 (A안 — 다음 로그인 시 강제 재인증)
        if (typeof window !== "undefined") {
          const clientId = process.env.NEXT_PUBLIC_KAKAO_CLIENT_ID;
          const origin = window.location.origin;
          if (clientId) {
            const redirectUri = `${origin}/login`;
            window.location.href =
              `https://kauth.kakao.com/oauth/logout?client_id=${clientId}` +
              `&logout_redirect_uri=${encodeURIComponent(redirectUri)}`;
            return;
          }
          // 카카오 키 없으면 그냥 홈으로
          window.location.href = "/";
        }
      },
    }),
    {
      name: "trendrain-auth",
      version: 2,
      storage: createJSONStorage(() => safeStorage),  // β-8 추적 방지 안전 wrapper
      partialize: (state) => ({ token: state.token, user: state.user, isLoggedIn: state.isLoggedIn }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
      migrate: (persistedState: any, version: number) => {
        if (version < 2 && persistedState) {
          return { ...persistedState };
        }
        return persistedState;
      },
    }
  )
);

// Slice 4 audit fix (2026-05-31) — 다중 탭 동기화. 1탭 로그아웃 시 2탭도 즉시 무효화
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key !== "trendrain-auth") return;
    if (e.newValue === null) {
      useAuthStore.setState({ token: null, user: null, isLoggedIn: false });
      return;
    }
    try {
      const parsed = JSON.parse(e.newValue);
      const next = parsed?.state ?? parsed;
      if (next) {
        useAuthStore.setState({
          token: next.token ?? null,
          user: next.user ?? null,
          isLoggedIn: !!next.isLoggedIn,
        });
      }
    } catch {
      // JSON parse 실패 — 무시
    }
  });
}
